import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { Button } from './button';

interface Step {
  title: string;
  content: React.ReactNode;
}

interface FormWizardProps {
  steps: Step[];
  onComplete: () => void;
  loading?: boolean;
  submitText?: string;
  submitIcon?: React.ReactNode;
  stepValidation?: boolean[]; // Array indicating which steps are valid
  currentStep?: number; // Allow external control of current step
  onStepChange?: (step: number) => void; // Callback when step changes
}

const FormWizard: React.FC<FormWizardProps> = ({
  steps,
  onComplete,
  loading = false,
  submitText = "Submit",
  submitIcon,
  stepValidation = [],
  currentStep: externalCurrentStep,
  onStepChange
}) => {
  const [internalCurrentStep, setInternalCurrentStep] = useState(0);
  const currentStep = externalCurrentStep !== undefined ? externalCurrentStep : internalCurrentStep;
  
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;
  const currentStepData = steps[currentStep];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      const newStep = currentStep + 1;
      if (onStepChange) {
        onStepChange(newStep);
      } else {
        setInternalCurrentStep(newStep);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      const newStep = currentStep - 1;
      if (onStepChange) {
        onStepChange(newStep);
      } else {
        setInternalCurrentStep(newStep);
      }
    }
  };

  const handleStepClick = (stepIndex: number) => {
    if (onStepChange) {
      onStepChange(stepIndex);
    } else {
      setInternalCurrentStep(stepIndex);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {steps.map((step, index) => (
            <div key={index} className="flex items-center">
              <button
                onClick={() => handleStepClick(index)}
                className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
                  index < currentStep
                    ? (stepValidation[index] === false ? 'bg-red-500 border-red-500 text-white' : 'bg-green-500 border-green-500 text-white')
                    : index === currentStep
                    ? (stepValidation[index] === false ? 'bg-red-500 border-red-500 text-white' : 'bg-blue-500 border-blue-500 text-white')
                    : stepValidation[index] === false
                    ? 'bg-red-100 border-red-300 text-red-600'
                    : 'bg-gray-100 border-gray-300 text-gray-500'
                }`}
              >
                {index < currentStep ? (
                  stepValidation[index] === false ? (
                    <span className="text-sm font-medium">{index + 1}</span>
                  ) : (
                    <Check className="w-5 h-5" />
                  )
                ) : (
                  <span className="text-sm font-medium">{index + 1}</span>
                )}
              </button>
              {index < steps.length - 1 && (
                <div
                  className={`w-16 h-1 mx-2 ${
                    index < currentStep 
                      ? (stepValidation[index] === false ? 'bg-red-300' : 'bg-green-500')
                      : 'bg-gray-300'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">
            {currentStepData.title}
          </h2>
          <p className="text-sm text-gray-500 mt-2">
            Step {currentStep + 1} of {steps.length}
          </p>
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        {currentStepData.content}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={handlePrevious}
          disabled={isFirstStep}
          className="flex items-center"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>

        <div className="flex gap-3">
          {steps.map((step, index) => (
            <button
              key={index}
              onClick={() => handleStepClick(index)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                index === currentStep
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {step.title}
            </button>
          ))}
        </div>

        {isLastStep ? (
          <Button
            type="button"
            onClick={onComplete}
            disabled={loading || stepValidation.includes(false)}
            className={`flex items-center ${
              stepValidation.includes(false) 
                ? 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed' 
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Submitting...
              </>
            ) : (
              <>
                {submitIcon && <span className="mr-2">{submitIcon}</span>}
                {submitText}
                <Check className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        ) : (
          <Button
            type="button"
            onClick={handleNext}
            className="flex items-center"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default FormWizard; 